import { InventoryCountItem } from './stock.types';

/**
 * Conteos crudos por estado sobre un conjunto de ítems. Es el vocabulario común de todo el desglose del
 * conteo (scoreboard global de la lista/sesión y agregados por categoría de la grilla): cada consumidor
 * los acumula y después los reencuadra a su gusto. Semántica por ítem en §5.5.
 */
export interface InventoryCountBuckets {
  /** Con control (`e !== null`). */
  controlled: number;
  /** Con control ya contados (`c !== null`) — incluye los contados en 0. */
  counted: number;
  /** Sin control (`e === null && c === null`). */
  noControl: number;
}

export interface InventoryCountScoreboard {
  /** Con control ya contados (`c !== null`) — incluye los contados en 0 (contar en 0 es contar). */
  counted: number;
  /** Con control (`e !== null`) todavía sin contar (`c === null`). */
  pending: number;
  /** Sin control (`e === null && c === null`). */
  noControl: number;
  /** Con control (`e !== null`) — denominador del progreso. */
  total: number;
}

/**
 * Clasifica un ítem en los buckets crudos (0/1 por bucket). Fuente única de la semántica por ítem. El
 * invariante canónico de "controlado" es `e !== null` (decisión LOCKED; el toggle de control del count-box
 * usa lo mismo). Contar un ítem sin control fija su esperado (`onCountChange`), así que el estado
 * `e === null && c !== null` no es alcanzable — no hace falta tratarlo aparte. Puro.
 */
export function classifyCountItem({ e, c }: InventoryCountItem): InventoryCountBuckets {
  const controlled = e !== null;
  const counted = controlled && c !== null;

  return {
    controlled: controlled ? 1 : 0,
    counted: counted ? 1 : 0,
    noControl: e === null && c === null ? 1 : 0,
  };
}

/** Acumula los buckets crudos de una colección de ítems. Puro. */
export function accumulateCountBuckets(items: Iterable<InventoryCountItem>): InventoryCountBuckets {
  const totals: InventoryCountBuckets = { controlled: 0, counted: 0, noControl: 0 };

  for (const item of items) {
    const buckets = classifyCountItem(item);

    totals.controlled += buckets.controlled;
    totals.counted += buckets.counted;
    totals.noControl += buckets.noControl;
  }

  return totals;
}

/**
 * Reencuadra los buckets crudos al scoreboard mostrable (contados, pendientes, sin control). Contados incluye
 * los contados en 0 (contar en 0 es contar): contados + pendientes + sin control = total de ítems, sin
 * solaparse. Compartido por la card de la lista y el header de la sesión de conteo, así ambos leen SIEMPRE el
 * mismo mapa `stockItems` y no divergen. Puro.
 */
export function toCountScoreboard(buckets: InventoryCountBuckets): InventoryCountScoreboard {
  return {
    counted: buckets.counted,
    pending: buckets.controlled - buckets.counted,
    noControl: buckets.noControl,
    total: buckets.controlled,
  };
}

/**
 * Desglose por estado del mapa `stockItems`, con la misma semántica por ítem que `buildCountView` (§5.5):
 * un ítem in-scope siempre está en el mapa, así que no hace falta el join con el catálogo. Alimenta tanto
 * el scoreboard de la sesión de conteo como los scorecards de la card de la lista. Puro.
 */
export function computeCountScoreboard(
  stockItems: Record<string, InventoryCountItem>,
): InventoryCountScoreboard {
  return toCountScoreboard(accumulateCountBuckets(Object.values(stockItems)));
}
